import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { EyeIcon, EyeOffIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import AuthLayout from '../components/AuthLayout';
const SignUp = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const {
    signup,
    isLoading
  } = useAuth();
  const navigate = useNavigate();
  // Password strength indicators
  const [passwordStrength, setPasswordStrength] = useState(0);
  const strengthLabels = ['Weak', 'Fair', 'Good', 'Strong'];
  const strengthColors = ['bg-red-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'];
  const checkPasswordStrength = (password: string) => {
    let strength = 0;
    // Length check
    if (password.length >= 8) strength += 1;
    // Contains number check
    if (/\d/.test(password)) strength += 1;
    // Contains special character check
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 1;
    // Contains uppercase and lowercase
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 1;
    return Math.min(3, strength);
  };
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordStrength(checkPasswordStrength(newPassword));
  };
  // Format phone number as user types
  const formatPhoneNumber = (value: string) => {
    // Strip all non-numeric characters
    const phoneNumber = value.replace(/\D/g, '');
    // Format the number as (XXX) XXX-XXXX
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedNumber = formatPhoneNumber(e.target.value);
    setPhoneNumber(formattedNumber);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!firstName || !lastName || !email || !phoneNumber || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    // Validate phone number format
    const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
    if (!phoneRegex.test(phoneNumber)) {
      setError('Please enter a valid phone number');
      return;
    }
    try {
      const success = await signup(firstName, lastName, email, phoneNumber, password);
      if (success) {
        navigate('/otp-verification', {
          state: {
            email,
            resetMode: false
          }
        });
      } else {
        setError('Email is already in use');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    }
  };
  return <AuthLayout data-id="element-924">
      <div className="w-full space-y-6" data-id="element-925">
        <div className="text-center" data-id="element-926">
          <h1 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-teal-600" data-id="element-927">
            SubTracker
          </h1>
          <h2 className="mt-6 text-center text-2xl font-bold text-gray-900" data-id="element-928">
            Create a new account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600" data-id="element-929">
            Or{' '}
            <Link to="/login" className="font-medium text-teal-600 hover:text-teal-500 transition-colors" data-id="element-930">
              sign in to existing account
            </Link>
          </p>
        </div>
        {error && <div className="p-4 rounded-md bg-red-50 text-red-700" data-id="element-931">{error}</div>}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit} data-id="element-932">
          <div className="space-y-4" data-id="element-933">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-934">
              <div data-id="element-935">
                <label htmlFor="first-name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-936">
                  First name
                </label>
                <input id="first-name" name="first-name" type="text" autoComplete="given-name" required className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-teal-500 focus:border-teal-500 transition-all" placeholder="John" value={firstName} onChange={e => setFirstName(e.target.value)} data-id="element-937" />
              </div>
              <div data-id="element-938">
                <label htmlFor="last-name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-939">
                  Last name
                </label>
                <input id="last-name" name="last-name" type="text" autoComplete="family-name" required className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-teal-500 focus:border-teal-500 transition-all" placeholder="Doe" value={lastName} onChange={e => setLastName(e.target.value)} data-id="element-940" />
              </div>
            </div>
            <div data-id="element-941">
              <label htmlFor="email-address" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-942">
                Email address
              </label>
              <input id="email-address" name="email" type="email" autoComplete="email" required className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-teal-500 focus:border-teal-500 transition-all" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} data-id="element-943" />
            </div>
            <div data-id="element-944">
              <label htmlFor="phone-number" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-945">
                Phone number
              </label>
              <input id="phone-number" name="phone-number" type="tel" autoComplete="tel" required className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-teal-500 focus:border-teal-500 transition-all" placeholder="(123) 456-7890" value={phoneNumber} onChange={handlePhoneChange} data-id="element-946" />
            </div>
            <div data-id="element-947">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-948">
                Password
              </label>
              <div className="relative" data-id="element-949">
                <input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required className="appearance-none block w-full pr-10 px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-teal-500 focus:border-teal-500 transition-all" placeholder="••••••••" value={password} onChange={handlePasswordChange} minLength={8} data-id="element-950" />
                <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600" onClick={() => setShowPassword(!showPassword)} data-id="element-951">
                  {showPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-952" /> : <EyeIcon className="h-5 w-5" data-id="element-953" />}
                </button>
              </div>
              {/* Password strength indicator */}
              {password.length > 0 && <div className="mt-2" data-id="element-954">
                  <div className="flex items-center justify-between mb-1" data-id="element-955">
                    <span className="text-xs font-medium text-gray-500" data-id="element-956">
                      Password strength:
                    </span>
                    <span className={`text-xs font-medium ${passwordStrength === 0 ? 'text-red-500' : passwordStrength === 1 ? 'text-yellow-500' : passwordStrength === 2 ? 'text-blue-500' : 'text-green-500'}`} data-id="element-957">
                      {strengthLabels[passwordStrength]}
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden" data-id="element-958">
                    <div className={`h-full ${strengthColors[passwordStrength]} transition-all duration-300 ease-in-out`} style={{
                  width: `${(passwordStrength + 1) * 25}%`
                }} data-id="element-959"></div>
                  </div>
                </div>}
            </div>
            <div data-id="element-960">
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-961">
                Confirm password
              </label>
              <div className="relative" data-id="element-962">
                <input id="confirm-password" name="confirm-password" type={showConfirmPassword ? 'text' : 'password'} autoComplete="new-password" required className={`appearance-none block w-full pr-10 px-4 py-3 border rounded-lg placeholder-gray-400 focus:outline-none focus:ring-teal-500 focus:border-teal-500 transition-all ${confirmPassword && password !== confirmPassword ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}`} placeholder="••••••••" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} data-id="element-963" />
                <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600" onClick={() => setShowConfirmPassword(!showConfirmPassword)} data-id="element-964">
                  {showConfirmPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-965" /> : <EyeIcon className="h-5 w-5" data-id="element-966" />}
                </button>
              </div>
              {confirmPassword && <div className="mt-1 flex items-center" data-id="element-967">
                  {password === confirmPassword ? <>
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-968" />
                      <span className="text-xs text-green-500" data-id="element-969">
                        Passwords match
                      </span>
                    </> : <>
                      <XCircleIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-970" />
                      <span className="text-xs text-red-500" data-id="element-971">
                        Passwords don't match
                      </span>
                    </>}
                </div>}
            </div>
          </div>
          <div data-id="element-972">
            <button type="submit" disabled={isLoading || confirmPassword && password !== confirmPassword} className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-all disabled:opacity-70" data-id="element-973">
              {isLoading ? <span className="flex items-center" data-id="element-974">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-975">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-976"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-977"></path>
                  </svg>
                  Creating account...
                </span> : 'Create Account'}
            </button>
          </div>
          <div className="text-center" data-id="element-978">
            <p className="text-xs text-gray-500" data-id="element-979">
              By creating an account, you agree to our{' '}
              <a href="#" className="text-teal-600 hover:text-teal-500" data-id="element-980">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="#" className="text-teal-600 hover:text-teal-500" data-id="element-981">
                Privacy Policy
              </a>
            </p>
          </div>
        </form>
      </div>
    </AuthLayout>;
};
export default SignUp;